import { CSSObject, Global } from '@emotion/react';
import {
  FloatingPortal,
  autoUpdate,
  offset as floatingOffset,
  shift,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useMergeRefs,
  useRole,
} from '@floating-ui/react';
import { useTheme } from '@hooks/useTheme';
import { TOOLTIP } from '@utils/zIndex';
import { isString } from 'lodash-es';
import {
  FC,
  HTMLAttributes,
  ReactElement,
  ReactNode,
  cloneElement,
  useState,
} from 'react';

const VISUAL_MARGIN = 8;

export type PositionType = 'centered' | 'bottom' | 'top' | 'topLeft' | 'left';

export interface TooltipProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  children: ReactElement;
  label?: ReactNode;
  ariaLabel?: string;
  hidden?: boolean;
  positionType?: PositionType;
  /** @deprecated Reach-era prop; ignored by the Floating UI implementation. Use positionType instead. */
  position?: (
    targetRect?: DOMRect | null,
    popoverRect?: DOMRect | null,
  ) => { left?: number; top?: number };
  /** @deprecated Reach-era prop kept for backward compatibility. */
  isVisible?: boolean;
}

// ─── Shared Styles ────────────────────────────────────────────────

const useTooltipStyles = (hidden?: boolean): CSSObject => {
  const { tooltip: tooltipTheme } = useTheme();
  return {
    ...tooltipTheme,
    border: 'none',
    borderRadius: 2,
    padding: '0.5em 1em',
    display: hidden ? 'none' : 'block',
    justifyContent: 'center',
    boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.2)',
    whiteSpace: 'pre-line',
  };
};

// ─── Tooltip (Floating UI) ────────────────────────────────────────

const positionToPlacement = {
  centered: {
    placement: 'bottom' as const,
    mainAxis: VISUAL_MARGIN,
    crossAxis: 0,
  },
  bottom: { placement: 'bottom-end' as const, mainAxis: 5, crossAxis: 5 },
  top: { placement: 'top' as const, mainAxis: 5, crossAxis: 0 },
  topLeft: { placement: 'top-end' as const, mainAxis: 5, crossAxis: 0 },
  left: { placement: 'bottom-start' as const, mainAxis: 5, crossAxis: 0 },
};

export const Tooltip: FC<TooltipProps> = ({
  children,
  label,
  ariaLabel,
  hidden,
  positionType,
  isVisible,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  position,
  style: styleProp,
  ...rest
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const tooltipStyles = useTooltipStyles(hidden);

  const { placement, mainAxis, crossAxis } =
    positionToPlacement[positionType || 'centered'];

  const { refs, floatingStyles, context } = useFloating({
    open: (isOpen || isVisible) && !!label && !hidden,
    onOpenChange: setIsOpen,
    placement,
    strategy: 'absolute',
    middleware: [
      floatingOffset({ mainAxis, crossAxis }),
      shift({ padding: 2 }),
    ],
    whileElementsMounted: autoUpdate,
  });

  const hover = useHover(context, { move: false });
  const focus = useFocus(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'tooltip' });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    focus,
    dismiss,
    role,
  ]);

  const childRef = (children as fixMe).ref;
  const mergedRef = useMergeRefs([refs.setReference, childRef]);

  const showTooltip = (isOpen || isVisible) && !!label && !hidden;

  return (
    <>
      <Global
        styles={{
          'html [data-reach-tooltip]': {
            zIndex: TOOLTIP,
          },
        }}
      />
      {cloneElement(
        children,
        getReferenceProps({
          // Spread the trigger child's own props so floating-ui composes (rather
          // than clobbers) any handlers the consumer already defined, e.g. focus
          // or click handlers on the trigger element.
          ...(children.props as anyOk),
          ref: mergedRef,
          // Bridge handlers: floating-ui's useHover listens for pointerenter,
          // but Cypress tests fire trigger('mouseover') which only produces
          // mouseenter via React event delegation. These bridge handlers
          // ensure the tooltip opens/closes from mouse events as well, while
          // still invoking any handler the trigger child provided.
          onMouseEnter: (e: anyOk) => {
            (children.props as anyOk).onMouseEnter?.(e);
            setIsOpen(true);
          },
          onMouseLeave: (e: anyOk) => {
            (children.props as anyOk).onMouseLeave?.(e);
            setIsOpen(false);
          },
          'data-reach-tooltip-trigger': '',
        }),
      )}
      {showTooltip && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            role="tooltip"
            data-reach-tooltip=""
            css={{ ...tooltipStyles, zIndex: TOOLTIP }}
            aria-label={ariaLabel ?? (isString(label) ? (label as string) : '')}
            {...rest}
            {...getFloatingProps()}
            style={{ ...floatingStyles, ...styleProp }}
          >
            {label}
          </div>
        </FloatingPortal>
      )}
    </>
  );
};

interface ButtonTooltipProps extends TooltipProps {
  hidden?: boolean;
  css?: CSSObject;
  isButtonEnabled?: boolean;
}

/** Helper component when you need to render a tooltip with a button as trigger. Without this component it can be difficult to properly trigger the tooltip display if the button is disabled (due to browser default pointer-events behavior for disabled elements) */
export const ButtonTooltip: FC<ButtonTooltipProps> = ({
  hidden,
  isButtonEnabled = false,
  children,
  css,
  ...rest
}) => (
  <div
    css={{
      display: 'inline-block',
      position: 'relative',
      cursor: hidden || isButtonEnabled ? 'pointer' : 'not-allowed !important',
      ...css,
    }}
  >
    {!isButtonEnabled && children}
    <Tooltip
      {...rest}
      hidden={hidden}
      label={
        isString(rest.label) ? (
          <div dangerouslySetInnerHTML={{ __html: rest.label }} />
        ) : (
          rest.label
        )
      }
    >
      {((): ReactElement => {
        if (isButtonEnabled) {
          return children;
        }
        if (hidden) {
          return <></>;
        }
        return (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
            }}
          />
        );
      })()}
    </Tooltip>
  </div>
);
